import { AmountInterface } from '../../../../layout/interfaces/amount-interface';
import { RateInterface } from '../../../../layout/interfaces/rate-interface';
import { LegacyModelDto, LegacyProductDto, LegacyRankDto } from '../../generated';
import { Addon } from './addon';
import { Person } from './person';
import { ProductCategory } from './product-category.enum';

export class Product implements LegacyProductDto {
  addons: Addon[] = [];
  ageCategoryId: number;
  defaultModelId: string;
  descriptionForAcceptedLanguages: {
    [key: string]: string;
  };
  fetchingPrice = true;
  freeChild: boolean;
  id: number;
  imagePath: string;
  models: LegacyModelDto[];
  name: {
    [key: string]: string;
  };
  nameWithMerchantForAcceptedLanguages: {
    [key: string]: string;
  };
  productCategoryId: ProductCategory;
  qualityCategoryAbbreviation: string;
  topSellerProduct: string;
  discount: number = 0;
  ageCategoryNameForAcceptedLanguages: { [key: string]: string };
  price: { [key: string]: number };

  qualityCategoryTranslationWithMerchant: string;
  rank: LegacyRankDto;
  qualityCategoryId: number;
  permitsFreeChild: boolean;
  definitionId: number;
  genderCategory: string;
  modelCount: number;
  defaultModelName: string;
  imagePathLarge: string;
  defaultModelImageSmall: string;
  defaultModelImageLarge: string;
  priceCurrencyCode: string;
  hasStopProduct = false;

  constructor(product: LegacyProductDto) {
    // Copy of product for "add to cart" function
    if (product.addons) {
      if (product instanceof Product) {
        product.addons
          .filter(addon => addon.isSelected)
          .forEach(addon => {
            this.addons.push(new Addon(addon));
          });
      } else {
        product.addons.forEach(addon => {
          this.addons.push(new Addon(addon));
        });
      }
    }

    this.ageCategoryId = product.ageCategoryId;
    this.defaultModelId = product.defaultModelId;
    this.descriptionForAcceptedLanguages = product.descriptionForAcceptedLanguages;
    this.id = product.id;
    this.definitionId = product.definitionId;
    this.imagePath = product.imagePath;
    this.models = product.models;
    this.name = product.name;
    this.nameWithMerchantForAcceptedLanguages = product.nameWithMerchantForAcceptedLanguages;
    this.productCategoryId = product.productCategoryId;
    this.qualityCategoryId = product.qualityCategoryId;
    this.qualityCategoryAbbreviation = product.qualityCategoryAbbreviation;
    this.topSellerProduct = product.topSellerProduct;
    this.freeChild = product.freeChild;
    this.ageCategoryNameForAcceptedLanguages = product.ageCategoryNameForAcceptedLanguages;
    this.price = product.price;
    this.defaultModelName = product.defaultModelName;
    this.rank = product.rank;
  }

  getAddonString(lang: string) {
    return this.addons.map(a => a.name[lang]).join();
  }

  getTotalDiscount(days: number, person: Person, rates: RateInterface, forCurrency: string) {
    return (
      1 -
      this.getTotalOnlinePrice(days, person, rates, forCurrency).amount /
        this.getTotalShopPrice(days, rates, forCurrency).amount
    );
  }

  getDiscount(person: Person): number {
    if (person.discounts) {
      return person.discounts.discounts[+this.productCategoryId][+this.qualityCategoryId]?.amount;
    } else {
      return 0;
    }
  }

  /**
   * returns the 'this-is-what-you'd-pay-in-the-store' price with selected addons. (the bad one to compare the total online price to)
   *
   * @param days number of days of the rental
   * @param rates the euro based exchange rates (for the euro based prices)
   * @param forCurrency the target currency you want to know the price for
   * @returns an AmountInterface containing the exchanges amound (without exchange rates)
   */
  getTotalShopPrice(days: number, rates: RateInterface, forCurrency: string): AmountInterface {
    let price = Math.round(+this.price[days] * rates.rates[forCurrency]);
    this.addons
      .filter(addon => addon.isSelected)
      .forEach(addon => {
        price += addon.getShopPrice(days, this, rates, forCurrency).amount; // Math.round(+addon.price[days] / rates.rates[forCurrency]);
      });
    return { amount: price, currency: { code: forCurrency, rates: {} } }; //+this.price[days];
  }

  /**
   * returns online price for this product (called in EUR) for given person.
   * The persons currency on total and the exchange rates are used to return
   * an AmountInterface in the given currency including exchange and discount rounding
   * @param person the person you want to know the price for
   * @returns AmountInterface
   */
  getOnlinePrice(days: number, person: Person, rates: RateInterface, forCurrency: string): AmountInterface {
    let reducedPrice = 0;
    if (person.discounts?.isFree && this.freeChild) {
      return { amount: reducedPrice, currency: { code: forCurrency, rates: {} } };
    }

    const basePrice = +this.price[days];
    if (person.discounts) {
      let discount = 0;
      try {
        discount = person.discounts.discounts[this.productCategoryId][this.qualityCategoryId].amount;
      } catch {
        console.info('no discount for product', this.productCategoryId, this.qualityCategoryId);
      }
      reducedPrice = Math.round(Math.round(basePrice * rates.rates[forCurrency]) * (1 - discount));
    }
    return { amount: reducedPrice, currency: { code: forCurrency, rates: {} } };
  }

  /**
   * sums up item and addons of a given person and returns amount in target currency.
   * For Total price in shop, see getTotalShopPrice(targetBasedRates: RateInterface).
   * Only works if products are requested as EUR. (they should always be requested as EUR)
   * @param person the person you wish to get the total reduced online price (person discount)
   * @param currency the target currency (of the customer) you want this sum to be
   * @param euroBasedRates the euro based rates (from the cart) you want to use to calculate
   * @returns AmountInterface in target currency
   */
  getTotalOnlinePrice(
    days: number,
    person: Person,
    rates: RateInterface,
    forCurrency: string
  ): AmountInterface {
    // exchange cents to "full" rounded pence, pennys, etc ->
    // apply discount -> to "full" rounded pence, pennys, etc
    // add amount of addons (in the same way)
    let total = this.getOnlinePrice(days, person, rates, forCurrency).amount;
    this.addons
      .filter(addon => addon.isSelected)
      .forEach(addOn => {
        // calculate price of relative priced addons like insurances
        total += addOn.getOnlinePrice(days, person, this, rates, forCurrency).amount;
      });

    return { amount: total, currency: { code: forCurrency, rates: {} } };
  }
}
